import { API_PATHS } from '@/constants/api-paths'
import { api, type AppRequestConfig } from '@/lib/api'
import { SERVICE_INSTANCE_PARAM } from '@/lib/service-instance'
import { withSessionRecovery } from '@/services/session-recovery'
import { useAuthStore } from '@/stores/auth-store'
import type { ApiResponse } from '@/types'

export interface OrgUserAddress {
  id: number
  building_name?: string | null
  address_line1?: string | null
  address_line2?: string | null
  postal_code?: string | null
  door_no?: string | null
  default?: boolean | null
  city?: string | null
  country_id?: number | null
  country_name?: string | null
  state_id?: number | null
  state_name?: string | null
}

export interface OrgUserAssignment {
  id: number
  role_id: number
  role_name?: string | null
  role_unique_id?: string | null
  branch_id?: number | null
  branch_instance_id?: string | null
  branch_name?: string | null
  branch_status?: number | null
  branch_status_label?: string | null
  service_variant_name?: string | null
  is_business_scope?: boolean
  status?: number | null
  status_label?: string | null
  is_selectable?: boolean
  is_active?: boolean
}

export interface OrgUserVisibilityAccess {
  id?: number | null
  access_id?: number | null
  instance_id?: string | null
  name?: string | null
  status?: number | null
  status_label?: string | null
  approved_on?: string | null
  created_on?: string | null
}

export interface OrgUserAdminRecord {
  id: number
  user_id: number
  created_on?: string | null
  usr_reg_num?: string | null
  country_id?: number | string | null
  country_code?: string | null
  country_name?: string | null
  fname?: string | null
  lname?: string | null
  title?: string | null
  username?: string | null
  status?: number | null
  registration_status?: number | null
  branch_access_status?: number | null
  branch_access_status_label?: string | null
  branch_access_id?: number | null
  is_active?: boolean
  deleted?: boolean
  can_delete?: boolean
  email?: string | null
  phone?: string | null
  date_of_birth?: string | null
  age?: number | null
  gender?: number | null
  exposure_notice_acknowledged_on?: string | null
  force_password_reset_pending?: boolean
  force_password_reset_expires_at?: string | null
  user_group?: number | null
  user_group_title?: string | null
  user_group_code?: string | null
  linked_job_count?: number
  linked_expense_count?: number
  linked_role_assignment_count?: number
  selectable_role_assignment_count?: number | null
  linked_worker_count?: number
  is_locked?: boolean
  retry_attempts_remaining?: number | null
  address_data?: OrgUserAddress | null
  addresses?: OrgUserAddress[]
  assignments?: OrgUserAssignment[]
  exposure_scope?: string | null
  origin_service_instance_id?: number | null
  origin_service_instance_instance_id?: string | null
  origin_service_instance_name?: string | null
  owner_service_instance_id?: number | null
  owner_service_instance_instance_id?: string | null
  owner_service_instance_name?: string | null
  visibility_accesses?: OrgUserVisibilityAccess[]
}

export interface OrgUserSignupConfig {
  signup_private_active?: boolean
  signup_public_fname: boolean
  signup_public_lname: boolean
  signup_public_mobile: boolean
  signup_public_email: boolean
  signup_public_country: boolean
  signup_public_state: boolean
  signup_public_dob: boolean
  signup_public_age: boolean
  signup_public_gender: boolean
}

export interface OrgUserCreatePayload {
  first_name: string
  last_name?: string
  email?: string
  phone_number?: string
  password?: string
  country_id?: number | string
  state_id?: number | string
  user_group?: number | string
  user_group_code?: string
  date_of_birth?: string
  age?: string
  gender?: string
  acknowledge_exposure_notice?: boolean
}

export interface OrgUserRegistrationStatusUpdatePayload {
  registration_status: number
}

export interface OrgUserBranchAccessStatusUpdatePayload {
  registration_status: number
}

export interface OrgUserUpdatePayload {
  first_name?: string
  last_name?: string
  email?: string
  phone_number?: string
  country_id?: number | string
  state_id?: number | string
  user_group?: number | string
  user_group_code?: string
  date_of_birth?: string
  age?: string
  gender?: string
}

type OrgUserAdminListPayload =
  | OrgUserAdminRecord[]
  | {
      results?: OrgUserAdminRecord[]
      public_user_count?: number
      private_user_count?: number
    }

export interface AssignableRole {
  id: string
  name: string
  created_on?: string | null
  unique_id?: string | null
  role_level?: number | null
  role_level_code?: string | null
  role_level_label?: string | null
  is_system?: boolean
  service_instance?: number | null
  service_variant?: string | number | null
  service_variant_name?: string | null
}

type AssignableRoleListPayload =
  | AssignableRole[]
  | {
      results?: AssignableRole[]
      data?: AssignableRole[]
    }

export interface AssociateBranchResponse {
  data: OrgUserVisibilityAccess
  message?: string
}

const extractPayload = <T>(payload: ApiResponse<T> | T): T => (
  typeof payload === 'object' && payload !== null && 'data' in payload
    ? payload.data
    : payload
) as T

const normalizeKey = (value: unknown) => String(value ?? '').trim()

const getPostServiceInstanceKey = (post: any) => (
  normalizeKey(post?.service_instance ?? post?.service_instance_id)
)

const isOrgUserPost = (post: any) => {
  const roleName = String(post?.role_name ?? '').trim().toLowerCase()
  if (roleName === 'org/user') {
    return true
  }

  const roleUniqueId = String(post?.roles_data?.unique_id ?? '').trim().toLowerCase()
  return roleUniqueId === 'org_user'
}

const resolveOrgUserPostId = (): string | null => {
  const authState = useAuthStore.getState()
  const currentPost = authState.currentPost
  const posts = [currentPost, ...(authState.availablePosts ?? [])].filter(Boolean)
  const activeServiceInstance = normalizeKey(
    authState.serviceInstanceId
    || currentPost?.service_instance
    || currentPost?.service_instance_id,
  )

  const scopedMatch = posts.find((post) => (
    isOrgUserPost(post) && (!activeServiceInstance || getPostServiceInstanceKey(post) === activeServiceInstance)
  ))

  const fallbackMatch = scopedMatch || posts.find((post) => isOrgUserPost(post))

  const match = fallbackMatch

  return match?.id != null ? String(match.id) : null
}

const withOrgUserPostConfig = (config: AppRequestConfig = {}): AppRequestConfig => {
  const postId = resolveOrgUserPostId()
  if (!postId) {
    return config
  }

  return {
    ...config,
    headers: {
      ...(config.headers ?? {}),
      'post-id': postId,
    },
  }
}

export const orgUserAdminApi = {
  list: async (options?: {
    search?: string
    includeInactive?: boolean
    page?: number
    pageSize?: number
    userGroupId?: string | number | null
    accessStatus?: string | number | null
    serviceInstanceId?: string | null
    orgInstanceId?: string | null
    branchInstanceId?: string | null
    customerScope?: 'all' | 'branch'
    orgUsers?: boolean
    source?: string | null
  }) => {
    const search = String(options?.search ?? '').trim()
    const page = options?.page && options.page > 0 ? options.page : 1
    const pageSize = options?.pageSize && options.pageSize > 0 ? options.pageSize : 50
    const userGroupId = options?.userGroupId != null ? String(options.userGroupId).trim() : ''
    const accessStatus = options?.accessStatus != null ? String(options.accessStatus).trim() : ''
    const orgServiceInstanceId = String(options?.serviceInstanceId ?? '').trim()
    const branchServiceInstanceId = String(options?.branchInstanceId ?? '').trim()
    const useBranchOnlyScope = options?.customerScope === 'branch' && branchServiceInstanceId.length > 0
    const scopedServiceInstanceId = useBranchOnlyScope ? branchServiceInstanceId : orgServiceInstanceId
    const orgUsersFlag = options?.orgUsers === true ? 1 : 0
    const source = String(options?.source ?? '').trim()
    if (!scopedServiceInstanceId) {
      throw new Error('service_instance_id is required for user list requests.')
    }

    return withSessionRecovery(async () => {
      // Deprecated route: /Org/User/Admin/List/. Consolidated to /Org/User/List/ as the single private user-list API.
      const response = await api.get<ApiResponse<OrgUserAdminListPayload> | OrgUserAdminListPayload>(API_PATHS.orgUsers.list, {
        params: {
          search,
          include_inactive: options?.includeInactive ? 1 : undefined,
          page,
          page_size: pageSize,
          user_group: userGroupId || undefined,
          user_group_id: userGroupId || undefined,
          access_status: accessStatus || undefined,
          service_instance_id: scopedServiceInstanceId,
          customer_scope: useBranchOnlyScope ? 'branch' : undefined,
          branch_only: useBranchOnlyScope ? 1 : undefined,
          org_user: orgUsersFlag,
          org_users: orgUsersFlag,
          source: source || undefined,
        },
        skipPostIdHeader: true,
        skipAuthRedirectOn401: true,
      } as any)

      const payload = extractPayload(response.data)
      if (Array.isArray(payload)) {
        return payload
      }

      return Array.isArray(payload?.results) ? payload.results : []
    })
  },

  listAll: async (options?: {
    search?: string
    includeInactive?: boolean
    userGroupId?: string | number | null
    accessStatus?: string | number | null
    serviceInstanceId?: string | null
    orgInstanceId?: string | null
    branchInstanceId?: string | null
    customerScope?: 'all' | 'branch'
    orgUsers?: boolean
    source?: string | null
    pageSize?: number
    maxPages?: number
  }) => {
    const pageSize = options?.pageSize && options.pageSize > 0 ? Math.min(options.pageSize, 1000) : 1000
    const maxPages = options?.maxPages && options.maxPages > 0 ? options.maxPages : 50

    const allRecords: OrgUserAdminRecord[] = []
    for (let page = 1; page <= maxPages; page += 1) {
      const batch = await orgUserAdminApi.list({
        ...options,
        page,
        pageSize,
      })
      allRecords.push(...batch)

      if (batch.length < pageSize) {
        break
      }
    }

    return allRecords
  },

  get: async (id: string | number, options?: { branchInstanceId?: string | null; serviceInstanceId?: string | null; orgInstanceId?: string | null }) => {
    return withSessionRecovery(async () => {
      const response = await api.get<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(API_PATHS.orgUserAdmin.detail(id), {
        params: {
          service_instance_id: options?.serviceInstanceId || undefined,
          org_instance_id: options?.orgInstanceId || options?.serviceInstanceId || undefined,
          branch_instance_id: options?.branchInstanceId || undefined,
        },
        skipPostIdHeader: true,
        skipAuthRedirectOn401: true,
      } as any)
      return extractPayload(response.data)
    })
  },

  listRoles: async (options?: {
    branchInstanceId?: string | null
    serviceInstanceId?: string | null
    orgInstanceId?: string | null
    serviceVariant?: string | number | null
    // Kept for call-site compatibility; Org/Roles/List ignores kind.
    kind?: 'job' | 'event' | 'committee' | 'task'
  }) => {
    return withSessionRecovery(async () => {
      const scopedServiceInstanceId = String(options?.serviceInstanceId ?? '').trim()
      const response = await api.get<ApiResponse<AssignableRoleListPayload> | AssignableRoleListPayload>(API_PATHS.orgRoles.list, {
        params: {
          service_instance_id: scopedServiceInstanceId || undefined,
          service_variant: options?.serviceVariant || undefined,
          page_size: 500,
        },
        headers: scopedServiceInstanceId
          ? { 'service-instance-id': scopedServiceInstanceId }
          : undefined,
        skipPostIdHeader: true,
        skipAuthRedirectOn401: true,
      } as any)
      const payload = extractPayload(response.data)
      const rawList = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.results)
          ? payload.results
          : (Array.isArray(payload?.data) ? payload.data : []))
      const normalizedRoles: AssignableRole[] = []
      for (const role of rawList as any[]) {
        const roleId = String(role?.id ?? '').trim()
        if (!roleId) {
          continue
        }

        normalizedRoles.push({
          id: roleId,
          name: String(role.name ?? role.title ?? ''),
          created_on: role.created_on ?? null,
          unique_id: role.unique_id ?? null,
          role_level: role.role_level ?? null,
          role_level_code: role.role_level_code ?? null,
          role_level_label: role.role_level_label ?? null,
          is_system: Boolean(role.is_system),
          service_instance: role.service_instance ?? null,
          service_variant: role.service_variant ?? role.service_variant_unique_id ?? null,
          service_variant_name: role.service_variant_name ?? role.service_variant_label ?? null,
        })
      }

      return normalizedRoles
    })
  },

  update: async (
    id: string | number,
    payload: OrgUserUpdatePayload,
    options?: { branchInstanceId?: string | null; serviceInstanceId?: string | null; orgInstanceId?: string | null },
  ) => {
    const config: AppRequestConfig = withOrgUserPostConfig()
    config.params = {
      ...(config.params as Record<string, unknown> | undefined),
      service_instance_id: options?.serviceInstanceId || undefined,
      org_instance_id: options?.orgInstanceId || options?.serviceInstanceId || undefined,
      branch_instance_id: options?.branchInstanceId || undefined,
    }
    const response = await api.patch<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(
      API_PATHS.orgUserAdmin.update(id),
      payload,
      config,
    )
    return extractPayload(response.data)
  },

  unlock: async (id: string | number, serviceInstanceId?: string | null) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(API_PATHS.orgUserAdmin.unlock(id), undefined, withOrgUserPostConfig(config))
    return extractPayload(response.data)
  },

  resetPin: async (id: string | number, serviceInstanceId?: string | null) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(API_PATHS.orgUserAdmin.resetPin(id), undefined, withOrgUserPostConfig(config))
    return extractPayload(response.data)
  },

  forcePasswordReset: async (id: string | number, serviceInstanceId?: string | null, password?: string) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const body = password ? { password } : undefined
    const response = await api.post<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(
      API_PATHS.orgUserAdmin.forcePasswordReset(id),
      body,
      withOrgUserPostConfig(config),
    )
    return extractPayload(response.data)
  },

  suspend: async (id: string | number, serviceInstanceId?: string | null) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(API_PATHS.orgUserAdmin.suspend(id), undefined, withOrgUserPostConfig(config))
    return extractPayload(response.data)
  },

  activate: async (id: string | number, serviceInstanceId?: string | null) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(API_PATHS.orgUserAdmin.activate(id), undefined, withOrgUserPostConfig(config))
    return extractPayload(response.data)
  },

  updateRegistrationStatus: async (
    id: string | number,
    payload: OrgUserRegistrationStatusUpdatePayload,
    serviceInstanceId?: string | null,
  ) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(
      API_PATHS.orgUserAdmin.updateRegistrationStatus(id),
      payload,
      withOrgUserPostConfig(config),
    )
    return extractPayload(response.data)
  },

  updateBranchAccessStatus: async (
    accessId: string | number,
    payload: OrgUserBranchAccessStatusUpdatePayload,
    serviceInstanceId?: string | null,
  ) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserVisibilityAccess> | OrgUserVisibilityAccess>(
      API_PATHS.orgUserAdmin.updateBranchAccessStatus(accessId),
      payload,
      withOrgUserPostConfig(config),
    )
    return extractPayload(response.data)
  },

  remove: async (id: string | number) => {
    const response = await api.delete(API_PATHS.orgUserAdmin.remove(id))
    return response.data
  },

  associateBranch: async (payload: { userId: number | string; serviceInstanceId: string; status?: number | null }) => {
    const config: AppRequestConfig = {}
    if (payload.serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: payload.serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserVisibilityAccess> | OrgUserVisibilityAccess>(
      API_PATHS.orgUserAdmin.associateBranch(payload.userId),
      {
        user_id: payload.userId,
        service_instance_id: payload.serviceInstanceId,
        status: payload.status ?? 1,
      },
      withOrgUserPostConfig(config),
    )
    const payloadData = extractPayload(response.data)
    const message = typeof response.data === 'object' && response.data !== null && 'message' in response.data
      ? String((response.data as ApiResponse<OrgUserVisibilityAccess>).message || '').trim()
      : ''
    return {
      data: payloadData,
      message: message || undefined,
    } as AssociateBranchResponse
  },

  approveBranchAssociation: async (accessId: number | string, serviceInstanceId?: string | null) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserVisibilityAccess> | OrgUserVisibilityAccess>(
      API_PATHS.orgUserAdmin.approveBranchAssociation(accessId),
      undefined,
      withOrgUserPostConfig(config),
    )
    return extractPayload(response.data)
  },

  rejectBranchAssociation: async (accessId: number | string, serviceInstanceId?: string | null) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.post<ApiResponse<OrgUserVisibilityAccess> | OrgUserVisibilityAccess>(
      API_PATHS.orgUserAdmin.rejectBranchAssociation(accessId),
      undefined,
      withOrgUserPostConfig(config),
    )
    return extractPayload(response.data)
  },

  detachBranchAssociation: async (accessId: number | string, serviceInstanceId?: string | null) => {
    const config: AppRequestConfig = {}
    if (serviceInstanceId) {
      config.params = { [SERVICE_INSTANCE_PARAM]: serviceInstanceId }
    }
    const response = await api.delete<ApiResponse<{ usage?: Record<string, number> }> | { usage?: Record<string, number> }>(
      API_PATHS.orgUserAdmin.detachBranchAssociation(accessId),
      withOrgUserPostConfig(config),
    )
    return extractPayload(response.data)
  },

  assignRole: async (payload: {
    userId: number | string
    roleId: number | string
    branchInstanceId?: string | null
    serviceInstanceId?: string | null
  }) => {
    return withSessionRecovery(async () => {
      const scopedServiceInstanceId = String(payload.serviceInstanceId ?? '').trim()
      const config: AppRequestConfig = withOrgUserPostConfig({
        ...(scopedServiceInstanceId
          ? { headers: { 'service-instance-id': scopedServiceInstanceId } }
          : {}),
      })
      if (scopedServiceInstanceId) {
        config.params = {
          ...(config.params as Record<string, unknown> | undefined),
          [SERVICE_INSTANCE_PARAM]: scopedServiceInstanceId,
        }
      }
      const response = await api.post<ApiResponse<OrgUserAssignment> | OrgUserAssignment>(
        API_PATHS.orgUserAdmin.assignmentAdd,
        {
          user_id: payload.userId,
          role_id: payload.roleId,
          branch_instance_id: payload.branchInstanceId || undefined,
        },
        config,
      )
      return extractPayload(response.data)
    })
  },

  removeAssignment: async (id: number | string) => {
    const response = await api.delete(API_PATHS.orgUserAdmin.assignmentRemove(id))
    return response.data
  },

  signupConfig: async () => {
    return withSessionRecovery(async () => {
      const response = await api.get<ApiResponse<OrgUserSignupConfig> | OrgUserSignupConfig>(API_PATHS.orgUserAdmin.signupConfig)
      return extractPayload(response.data) as OrgUserSignupConfig
    })
  },

  create: async (payload: OrgUserCreatePayload) => {
    const response = await api.post<ApiResponse<OrgUserAdminRecord> | OrgUserAdminRecord>(
      API_PATHS.orgUserAdmin.create,
      payload,
      withOrgUserPostConfig(),
    )
    return extractPayload(response.data)
  },
}